/**
 * Resilient fetch helpers for browser-hostile endpoints.
 *
 * Many useful endpoints (LNURL servers, personal blogs) don't send CORS
 * headers, so direct browser fetches fail even though the resource is fine.
 * Try direct first, then public CORS proxies.
 */

const CORS_PROXIES = [
  (url: string) => `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`,
  (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
];

/** Fetch a text resource (HTML etc.), direct first then via proxies. Null on failure. */
export async function fetchTextResilient(
  url: string,
  timeoutMs = 10_000,
  maxBytes = 512 * 1024,
): Promise<string | null> {
  const targets = [url, ...CORS_PROXIES.map((wrap) => wrap(url))];

  for (const target of targets) {
    try {
      const res = await fetch(target, { signal: AbortSignal.timeout(timeoutMs) });
      if (!res.ok || !res.body) continue;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let text = '';

      while (text.length < maxBytes) {
        const { done, value } = await reader.read();
        if (done) break;
        text += decoder.decode(value, { stream: true });
      }
      try {
        await reader.cancel();
      } catch {
        // Stream already closed — fine.
      }

      return text;
    } catch {
      // Try the next strategy.
    }
  }

  return null;
}

/** Fetch a JSON resource, direct first then via proxies. Null on failure. */
export async function fetchJsonResilient(url: string, timeoutMs = 10_000): Promise<unknown | null> {
  const text = await fetchTextResilient(url, timeoutMs);
  if (text === null) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}
